import { auth } from './auth';
import { tabStore } from './tabStore';
import { whoFromToken } from './session';

/*
 * Following iam's one-time Keycloak sign-in link (sign_in_url) for a NEW
 * account. Keycloak's extension never replaces a session that belongs to
 * someone else ("already authenticated as a different user"), and must not:
 * so if this browser's Keycloak session is someone else's, it is ended first,
 * the link waiting in this tab, and /signup/continue follows it on the way
 * back. The link lives about a minute: an old one is not followed, the org
 * step carries on with the session signup already stored.
 */

const KEY = 'dodil_signin_link';
/** A little under the link's own life, so it's never followed once it can't work. */
export const LINK_FRESH_MS = 50_000;

/** Keycloak's session, if it belongs to someone other than this signup's account (else undefined). */
export async function someoneElsesKeycloakSession(newEmail?: string): Promise<{ sub: string; email?: string; idToken?: string } | undefined> {
    const holder = await auth.keycloakSession();
    const me = whoFromToken(auth.getAccessToken());
    const someoneElse = holder && holder !== 'unknown'
        && holder.sub !== me?.sub
        && (!newEmail || holder.email?.toLowerCase() !== newEmail.toLowerCase());
    return someoneElse ? holder : undefined;
}

export async function followSignInLink(link: string, newEmail: string | undefined): Promise<void> {
    const holder = await someoneElsesKeycloakSession(newEmail);
    if (holder) {
        tabStore.set(KEY, JSON.stringify({ link, at: Date.now() }));
        auth.endKeycloakSession(holder, `${window.location.origin}/signup/continue`);
        return;
    }
    window.location.replace(link);
}

/** On the way back from ending the other session: the link, once, if it's still fresh. */
export function takeWaitingLink(now = Date.now()): string | undefined {
    const raw = tabStore.get(KEY);
    tabStore.remove(KEY);
    try {
        const { link, at } = JSON.parse(raw ?? 'null') ?? {};
        return typeof link === 'string' && typeof at === 'number' && now - at < LINK_FRESH_MS ? link : undefined;
    } catch {
        return undefined;
    }
}
